import { Link } from 'react-router-dom';
import { formatDate, formatViews, type Movie } from '@/lib/movies';

interface MovieCardProps {
  movie: Movie;
  showSynopsis?: boolean;
}

export default function MovieCard({ movie, showSynopsis = true }: MovieCardProps) {
  return (
    <Link
      to={`/detail/${movie.id}`}
      className="group block cursor-pointer animate-fade-up"
      aria-label={`查看影片 ${movie.title}`}
    >
      <div className="relative w-full aspect-video overflow-hidden rounded-lg bg-background-200">
        <img
          src={movie.cover}
          alt={`${movie.title} 影片封面`}
          title={`${movie.title} - 高清影片封面`}
          className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.06]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50/95 via-background-50/10 to-transparent" />

        <div className="absolute top-3 left-3 flex items-center gap-1.5 h-6 px-2 rounded-md bg-background-50/80 backdrop-blur-sm text-[11px] text-primary-400">
          <i className="ri-star-fill w-3.5 h-3.5 flex items-center justify-center text-[11px]" />
          <span className="font-label tracking-wide">{movie.rating.toFixed(1)}</span>
        </div>

        <div className="absolute top-3 right-3 h-6 px-2 rounded-md bg-accent-500/90 text-background-50 flex items-center text-[10px] tracking-widest font-label">
          HD
        </div>

        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <span className="w-12 h-12 rounded-full bg-primary-500/95 text-background-50 dark:text-foreground-950 flex items-center justify-center">
            <i className="ri-play-fill text-2xl w-6 h-6 flex items-center justify-center" />
          </span>
        </div>

        <div className="absolute bottom-2.5 left-3 right-3 flex items-center justify-between text-[11px] text-foreground-800">
          <span className="inline-flex items-center gap-1">
            <i className="ri-time-line w-3.5 h-3.5 flex items-center justify-center" />
            {movie.duration} 分钟
          </span>
          <span className="inline-flex items-center gap-1">
            <i className="ri-eye-line w-3.5 h-3.5 flex items-center justify-center" />
            {formatViews(movie.views)}
          </span>
        </div>
      </div>

      <div className="mt-3">
        <h3 className="text-sm md:text-base font-medium text-foreground-950 leading-snug line-clamp-1 group-hover:text-primary-400 transition-colors">
          {movie.title}
        </h3>
        {showSynopsis && (
          <p className="mt-1.5 text-xs leading-relaxed text-foreground-500 line-clamp-2">{movie.synopsis}</p>
        )}
        <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-foreground-600">
          <span>{movie.year}</span>
          <span className="w-1 h-1 rounded-full bg-background-400" />
          <span>{movie.region}</span>
          <span className="w-1 h-1 rounded-full bg-background-400" />
          <span className="inline-flex items-center gap-1">
            <i className="ri-refresh-line w-3 h-3 flex items-center justify-center" />
            {formatDate(movie.updatedAt)}
          </span>
        </div>
      </div>
    </Link>
  );
}